import { queryClient } from '@/router'
import { useMutation } from '@tanstack/react-query'
import { CalendarClockIcon, CheckSquareIcon, FileTextIcon, HashIcon, SmileIcon, UserIcon } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/button/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/table/table'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { updateTaskStatusMutation } from '@/lib/queries/business/v1/task/complete/mutations'
import { type DashboardTaskItem } from '@/lib/queries/business/v1/task/search/types'
import { cn } from '@/lib/utils'

import { CellContentActionList } from './table-cell-content/cell-content-action-list/cell-content-action-list'
import { CellContentAssignees } from './table-cell-content/cell-content-assignees'
import { CellContentCaseLink } from './table-cell-content/cell-content-case-link'

export interface ActionRequiredTableProps {
  data: DashboardTaskItem[]
}

export const ActionRequiredTable = ({ data }: ActionRequiredTableProps) => {
  const { mutateAsync: changeTaskStatus } = useMutation(updateTaskStatusMutation)

  const [nowUpdatingTaskIds, setNowUpdatingTaskIds] = useState<number[]>([])

  const handleClickCompleteButton = async (taskId: number) => {
    setNowUpdatingTaskIds((prev) => [...prev, taskId])
    await changeTaskStatus(
      [
        {
          id: taskId,
          isComplete: true,
        },
      ],
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: [API_ENDPOINTS.business.v1.task.search.index],
          })
          toast.success('Task has been completed!')
        },
        onError: () => {
          toast.error('Failed to complete task. Please try again later.')
        },
        onSettled: () => {
          setNowUpdatingTaskIds((prev) => prev.filter((id) => id !== taskId))
        },
      }
    )
  }

  if (data.length === 0) {
    return <p className={cn('text-center text-gray-500', 'py-3')}>No tasks yet.</p>
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className={cn('hover:bg-transparent')}>
          <TableHead>
            <p className={cn('flex items-center gap-x-1')}>
              <SmileIcon size={16} className={cn('min-w-4')} />
              Applicant
            </p>
          </TableHead>
          <TableHead>
            <p className={cn('flex items-center gap-x-1')}>
              <FileTextIcon size={16} className={cn('min-w-4')} />
              Case Type
            </p>
          </TableHead>
          <TableHead>
            <p className={cn('flex items-center gap-x-1')}>
              <HashIcon size={16} className={cn('min-w-4')} />
              Case Number
            </p>
          </TableHead>
          <TableHead>
            <p className={cn('flex items-center gap-x-1')}>
              <UserIcon size={16} className={cn('min-w-4')} />
              Assignee
            </p>
          </TableHead>
          <TableHead>
            <p className={cn('flex items-center gap-x-1')}>
              <CalendarClockIcon size={16} className={cn('min-w-4')} />
              Pending Day
            </p>
          </TableHead>
          <TableHead>
            <p className={cn('flex items-center gap-x-1')}>
              <CheckSquareIcon size={16} className={cn('min-w-4')} />
              Status
            </p>
          </TableHead>
          {/* Intentional empty cell */}
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map(({ applicantName, applicantPathId, caseType, caseNumber, assigneeNames, pendingDays, taskId }) => (
          <TableRow key={taskId} className={cn('hover:bg-transparent', 'text-slate-900')}>
            <TableCell>{applicantName}</TableCell>
            <TableCell>{caseType}</TableCell>
            <TableCell>
              <CellContentCaseLink applicantId={applicantPathId} caseNumber={caseNumber} />
            </TableCell>
            <TableCell>
              <CellContentAssignees assignees={assigneeNames} />
            </TableCell>
            <TableCell>
              {typeof pendingDays !== 'number' && '-'}
              {typeof pendingDays === 'number' && `${pendingDays} ${pendingDays >= 2 ? 'days' : 'day'}`}
            </TableCell>
            <TableCell>
              <CellContentActionList taskId={taskId} />
            </TableCell>
            <TableCell className={cn('text-right')}>
              <Button
                type="button"
                variant="default"
                size="sm"
                loading={nowUpdatingTaskIds.includes(taskId)}
                onClick={() => handleClickCompleteButton(taskId)}
              >
                Complete
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
